import React, { useEffect, useState, useMemo } from "react";
import { Table, Breadcrumb, Select, message, Spin, DatePicker, Tooltip } from "antd";
import dayjs from "dayjs";
import {
  CapitalizeFirstWord,
  useDebounce,
  rangePickerProps,
  rangePresets,
  buildAllowanceYearOptions,
  getBusinessCurrentYear,
} from "../../../helpers/utils";
import {
  convertLocalDayjsToBusinessUTC,
  convertBusinessUTCToLocalDayjs,
  formatDateWithBusinessTZ,
  formatCalendarDateDisplay,
  formatTimeOffSlot,
  formatTimeOffDuration,
  getTimeOffTimeRange,
} from "../../../helpers/moment";
import { useParams, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { svg } from "../../../Assets/svg";
import ClearComponent from "../../Components/ClearComponent";
import AutoCloseTooltip from "../../Components/AutoCloseTooltip";
import NoDataMessage from "../../Components/NoData";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { GetPtoHistoryAPI, GetEmployeeDetailsAPI } from "../../Redux/teamMember";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import InfiniteScroll from "react-infinite-scroll-component";
import FullScreenLoader from "../../Components/FullScreenLoader";
import { Capacitor } from "@capacitor/core";
import TimeOffTypeBadge from "../../Components/TimeOffTypeBadge";

import { getTimeOffTypesAPI } from "../../Redux/timeOffType";
import { getDecryptedUserData, decryptByText } from "../../../helpers/bycrypt";

const DataItem = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <div className="mb-0">
    <label className="custom-label mb-0 ff text-949494">{label}</label>
    <div className="mb-0 fs-6 fw-semibold d-block text-dark">
      {value ? value : "-"}
    </div>
  </div>
);

const PAGE_LIMIT = 30;

const PtoHistory: React.FC<{ isEmbedded?: boolean; pagePermission?: any }> = ({
  isEmbedded = false,
  pagePermission = PagePermissionNameData?.TEAM_MEMBERS,
}: any) => {
  const { RangePicker }: any = DatePicker;
  const userData = getDecryptedUserData();
  const currentYear = getBusinessCurrentYear();
  const { authLoading } = useAppSelector((state: RootState) => state?.loading);
  const USER_PERMISSIONS = usePermissionsChecker(pagePermission);
  const dispatch = useAppDispatch();
  const user = getDecryptedUserData();
  const { employeeDetails } = useAppSelector((state: RootState) => state.teamMember);

  const { id: paramsId } = useParams();

  const id = paramsId || user?._id;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const PTO_HISTORY_YEAR_KEY = "pto_history_year";

  const getInitialYear = (): string => {
    // 1. URL search params take primary precedence
    const paramYear = searchParams.get("year");
    if (paramYear !== null) {
      return paramYear.toLowerCase() === "all" ? "" : paramYear;
    }
    // 2. Navigation state
    const stateYear =
      (location.state as any)?.selectedYear ?? (location.state as any)?.year;
    if (stateYear !== undefined && stateYear !== null) {
      const s = String(stateYear);
      return s.toLowerCase() === "all" ? "" : s;
    }
    // 3. Session storage
    if (id) {
      const saved = sessionStorage.getItem(`${PTO_HISTORY_YEAR_KEY}_${id}`);
      if (saved !== null) {
        return saved.toLowerCase() === "all" ? "" : saved;
      }
    }
    // 4. Default to current year
    return currentYear.toString();
  };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [totalDays, setTotalDays] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>(getInitialYear);
  const [availableYears, setAvailableYears] = useState<(number | string)[]>([]);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [startDate, setStartDate] = useState<any>("");
  const [endDate, setEndDate] = useState<any>("");
  const [selectedLeaveType, setSelectedLeaveType] = useState<any>("");

  const handleYearFilterChange = (val: string) => {
    setSelectedYear(val);
    if (id) {
      sessionStorage.setItem(`${PTO_HISTORY_YEAR_KEY}_${id}`, val || "all");
    }
    if (!isEmbedded) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (val) {
            next.set("year", val);
          } else {
            next.set("year", "all");
          }
          return next;
        },
        {
          replace: true,
          state: {
            ...(location.state as any),
            year: val,
            selectedYear: val,
          },
        },
      );
    }
  };

  useEffect(() => {
    if (id) {
      sessionStorage.setItem(
        `${PTO_HISTORY_YEAR_KEY}_${id}`,
        selectedYear || "all",
      );
    }
  }, [selectedYear, id]);

  useEffect(() => {
    const stateYear =
      (location.state as any)?.selectedYear ?? (location.state as any)?.year;
    if (stateYear !== undefined && stateYear !== null) {
      const s = String(stateYear).toLowerCase() === "all" ? "" : String(stateYear);
      if (s !== selectedYear) {
        setSelectedYear(s);
        if (id) {
          sessionStorage.setItem(`${PTO_HISTORY_YEAR_KEY}_${id}`, s || "all");
        }
      }
    }
  }, [location.state, selectedYear, id]);

  useEffect(() => {
    if (isEmbedded) return;
    const currentParam = searchParams.get("year");
    const targetParam = selectedYear ? selectedYear : "all";
    if (currentParam !== targetParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("year", targetParam);
          return next;
        },
        { replace: true },
      );
    }
  }, [selectedYear, searchParams, setSearchParams, isEmbedded]);

  useEffect(() => {
    const paramYear = searchParams.get("year");
    if (paramYear !== null) {
      const normalizedParamYear =
        paramYear.toLowerCase() === "all" ? "" : paramYear;
      if (normalizedParamYear !== selectedYear) {
        setSelectedYear(normalizedParamYear);
      }
      if (id) {
        sessionStorage.setItem(
          `${PTO_HISTORY_YEAR_KEY}_${id}`,
          normalizedParamYear || "all",
        );
      }
    }
  }, [searchParams, selectedYear, id]);

  const disabledDate = (current: any) => {
    if (!selectedYear) return false;
    const yearNum = Number(selectedYear);
    if (isNaN(yearNum)) return false;
    return current ? current.year() !== yearNum : false;
  };

  const defaultPickerValue = useMemo(() => {
    if (startDate && endDate) {
      return undefined;
    }
    if (selectedYear) {
      const y = Number(selectedYear);
      if (!isNaN(y)) {
        if (y === currentYear || y === dayjs().year()) {
          return undefined;
        }
        return [dayjs(`${y}-01-01`), dayjs(`${y}-02-01`)];
      }
    }
    return undefined;
  }, [selectedYear, currentYear, startDate, endDate]);

  const activePresets = useMemo(() => {
    if (!selectedYear || Number(selectedYear) === currentYear) {
      return rangePresets;
    }
    const y = Number(selectedYear);
    if (isNaN(y)) return undefined;
    return [
      { label: "Full Year", value: [dayjs(`${y}-01-01`), dayjs(`${y}-12-31`)] },
      { label: "Q1", value: [dayjs(`${y}-01-01`), dayjs(`${y}-03-31`)] },
      { label: "Q2", value: [dayjs(`${y}-04-01`), dayjs(`${y}-06-30`)] },
      { label: "Q3", value: [dayjs(`${y}-07-01`), dayjs(`${y}-09-30`)] },
      { label: "Q4", value: [dayjs(`${y}-10-01`), dayjs(`${y}-12-31`)] },
    ];
  }, [selectedYear, currentYear]);

  useEffect(() => {
    if (selectedYear && startDate) {
      const startDayjs = convertBusinessUTCToLocalDayjs(startDate);
      if (startDayjs && startDayjs.year() !== Number(selectedYear)) {
        setStartDate("");
        setEndDate("");
      }
    }
  }, [selectedYear, startDate]);

  const [leaveTypes, setLeaveTypes] = useState<any[]>([]);

  const [hasMore, setHasMore] = useState(true);

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const getFilterCount = () => {
    let count = 0;
    if (selectedType) count++;
    if (selectedAction) count++;
    if (selectedLeaveType) count++;
    if (selectedYear && selectedYear !== currentYear.toString()) count++;
    return count;
  };

  const clearFilters = () => {
    setSelectedType("");
    setSelectedAction("");
    setSelectedLeaveType("");
    handleYearFilterChange(currentYear.toString());
  };

  const onRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(
        convertLocalDayjsToBusinessUTC(dates[0].startOf("day")) || "",
      );
      setEndDate(convertLocalDayjsToBusinessUTC(dates[1].endOf("day")) || "");
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const fetchHistory = (page: number) => {
    if (!id) return;
    setLoading(true);
    dispatch(
      GetPtoHistoryAPI(
        {
          user: id,
          page,
          limit: PAGE_LIMIT,
          search: debouncedSearch,
          type: selectedType,
          year: selectedYear,
          action: selectedAction,
          start_date: startDate || "",
          end_date: endDate || "",
          leave_type: selectedLeaveType,
          is_manage_allowance: true,
        },
        (resData: any) => {
          const newData = resData?.data || [];
          if (page === 1) {
            setData(newData);
            if (Array.isArray(resData?.years)) {
              setAvailableYears(resData.years);
            }
            if (typeof resData?.totalDays === "number") {
              setTotalDays(resData.totalDays);
            } else {
              let deducted = 0;
              let reverted = 0;
              newData.forEach((item: any) => {
                const amt = Number(item?.amount) || 0;
                if (item?.action === "reverted") reverted += amt;
                else deducted += amt;
              });
              let calculated = 0;
              if (selectedAction === "reverted") calculated = reverted;
              else if (selectedAction === "deducted") calculated = deducted;
              else calculated = Math.max(0, deducted - reverted);
              setTotalDays(Math.round(calculated * 100) / 100);
            }
          } else {
            setData((prev) => [...prev, ...newData]);
            if (typeof resData?.totalDays === "number") {
              setTotalDays(resData.totalDays);
            }
          }
          setTotal(resData?.total || 0);
          setHasMore(newData.length === PAGE_LIMIT);
          setLoading(false);
        },
      ),
    );
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchHistory(1);
  }, [
    id,
    debouncedSearch,
    selectedType,
    selectedYear,
    selectedAction,
    startDate,
    endDate,
    selectedLeaveType,
  ]);

  useEffect(() => {
    if (id) {
      dispatch(GetEmployeeDetailsAPI(id) as any);
    }
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(
      getTimeOffTypesAPI({
        onSuccess: (resData: any[]) => {
          setLeaveTypes(resData || []);
        },
        onError: () => {
          setLeaveTypes([]);
        },
      }),
    );
  }, [dispatch]);

  const loadMoreData = () => {
    if (!loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchHistory(nextPage);
    }
  };

  const tableData = data;

  const columns = [
    {
      title: "Date",
      key: "date",
      render: (record: any) => {
        const start = record.timeOffRequest?.starting?.date || record.date;
        if (!start) return "-";
        const dateStr = formatCalendarDateDisplay(start, "DD-MM-YYYY");
        const rangeTooltip = record.timeOffRequest ? getTimeOffTimeRange(record.timeOffRequest) : "";

        return (
          <AutoCloseTooltip title={rangeTooltip} trigger={["click", "hover"]}>
            <span style={{ whiteSpace: "nowrap" }} className="cursor-pointer">
              {dateStr}
            </span>
          </AutoCloseTooltip>
        );
      },
    },
    {
      title: "Duration",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number, record: any) => {
        const durationText = record.timeOffRequest
          ? formatTimeOffDuration(record.timeOffRequest, amount)
          : amount
            ? `${amount} (${Number(amount) > 1 ? "Days" : "Day"})`
            : "-";
        const rangeTooltip = record.timeOffRequest ? getTimeOffTimeRange(record.timeOffRequest) : "";

        return (
          <AutoCloseTooltip title={rangeTooltip}>
            <span style={{ whiteSpace: "nowrap" }} className="cursor-pointer">
              {durationText}
            </span>
          </AutoCloseTooltip>
        );
      },
    },
    {
      title: "Type",
      key: "requestType",
      render: (record: any) => (
        <div className="d-flex align-items-center gap-2 text-nowrap">
          <span>{record.timeOffRequest?.type?.name || (record.type === "holiday" ? "Holiday" : "-")}</span>
          {record.timeOffRequest?.type?.type && (
            <TimeOffTypeBadge type={record.timeOffRequest.type.type} />
          )}
        </div>
      ),
    },
    {
      title: "Deduction From",
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {type === "paid_leave"
            ? "Time Off Allowance"
            : type === "unpaid_leave"
              ? "Unpaid Time Off Allowance"
              : type === "holiday"
                ? "Annual Time Off Allowance"
                : "-"}
        </span>
      ),
    },

    {
      title: "Created On",
      key: "created_at",
      render: (record: any) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {formatDateWithBusinessTZ(record.created_at, "DD-MM-YYYY, hh:mm A")}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (action: string) => (
        <span
          className={
            action === "deducted"
              ? "text-danger text-capitalize"
              : "text-success text-capitalize"
          }
        >
          {action === "deducted"
            ? "Deducted (Approved)"
            : action === "reverted"
              ? "Reverted (Rejected)"
              : CapitalizeFirstWord(action)}
        </span>
      ),
    },
  ];

  const extraYears = useMemo(() => {
    const list: (number | string)[] = [];
    if (selectedYear) list.push(selectedYear);
    if (Array.isArray(employeeDetails?.allowances)) {
      employeeDetails.allowances.forEach((item: any) => {
        if (item?.year) list.push(item.year);
      });
    }
    return list;
  }, [selectedYear, employeeDetails?.allowances]);

  const yearOptions = useMemo(
    () =>
      buildAllowanceYearOptions(
        availableYears.length > 0 ? availableYears : extraYears,
      ),
    [availableYears, extraYears],
  );

  const selectedYearPto = useMemo(() => {
    if (selectedYear) {
      if (Array.isArray(employeeDetails?.allowances)) {
        const match = employeeDetails.allowances.find(
          (item: any) => Number(item?.year) === Number(selectedYear),
        );
        if (match) return match?.pto || 0;
      }
      if (Number(selectedYear) === currentYear) {
        return employeeDetails?.allowance?.pto || 0;
      }
      return 0;
    }
    if (
      Array.isArray(employeeDetails?.allowances) &&
      employeeDetails.allowances.length > 0
    ) {
      return employeeDetails.allowances.reduce(
        (acc: number, item: any) => acc + (Number(item?.pto) || 0),
        0,
      );
    }
    return employeeDetails?.allowance?.pto || 0;
  }, [employeeDetails?.allowances, employeeDetails?.allowance, selectedYear, currentYear]);

  const hasActiveFilters = Boolean(
    (startDate && endDate) ||
    debouncedSearch ||
    selectedLeaveType ||
    selectedType ||
    selectedAction ||
    (selectedYear && selectedYear !== currentYear.toString())
  );

  const displayDays = totalDays !== null ? totalDays : (hasActiveFilters ? 0 : selectedYearPto);

  if (authLoading) {
    return <FullScreenLoader />;
  }

  if (USER_PERMISSIONS?.READ === false) {
    return <NoPermissionPage />;
  }

  return (
    <div className={`h-100 ${isEmbedded ? "" : "page-content px-40 py-40"}`}>
      <div className="d-flex flex-column gap-md-2 gap-0">
        {isEmbedded && (
          <div>
            <div
              className="mb-3 d-flex justify-content-between px-3 py-3 rounded-2"
              style={{ backgroundColor: "#ffffffff" }}
            >
              <label className="custom-label mb-0 ff text-949494">
                Total Used Time Off
              </label>
              <div className="mb-0 fs-6 fw-semibold d-block text-dark">
                {displayDays} {displayDays === 1 ? "Day" : "Days"}{" "}
              </div>
            </div>
          </div>
        )}
        <div className="d-flex flex-lg-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center mb-1">
          <h2 className="text-black fs-36 fw-bolder page-title mb-0 d-flex align-items-center gap-3 order-1 order-lg-1">
            {!isEmbedded && (
              <span
                className="cursor-pointer svg-24"
                onClick={() => navigate(-1)}
              >
                {svg?.backArrow || "←"}
              </span>
            )}
            <span>
              Total Used Time Off
              {displayDays !== null && Number(displayDays) > 0
                ? ` (${displayDays} ${Number(displayDays) === 1 ? "Day" : "Days"})`
                : ""}
            </span>
          </h2>

          <div className="d-flex flex-sm-nowrap flex-wrap align-items-center gap-md-3 gap-2 order-3 order-lg-2 ms-lg-auto w-tablet-100">
            <div className="pageSearch mw-100 position-relative d-inline-block w-mob-100">
              <input
                type="text"
                value={search}
                className="form-control form-control-lg"
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Type"
              />
              {search?.length ? (
                <span className="cursor-pointer" onClick={() => setSearch("")}>
                  {svg?.searchClear}
                </span>
              ) : (
                <span>{svg?.search}</span>
              )}
            </div>

            <div
              className={`dateSelectFilter RangeDatePicker timeOffDatePicker border-s mw-100 d-inline-block p-0 w-mob-100 ms-sm-auto ms-lg-0 ${!startDate ? "not-active" : "is-active"
                }`}
            >
              <RangePicker
                presets={activePresets}
                onChange={onRangeChange}
                disabledDate={disabledDate}
                defaultPickerValue={defaultPickerValue as any}
                {...rangePickerProps}
                value={
                  startDate && endDate
                    ? [
                      convertBusinessUTCToLocalDayjs(startDate),
                      convertBusinessUTCToLocalDayjs(endDate),
                    ]
                    : null
                }
                inputReadOnly={Capacitor.isNativePlatform()}
              />
            </div>
          </div>

          <div className="d-flex gap-md-3 gap-2 order-2 order-lg-3 ms-auto ms-lg-0">
            <div
              onClick={toggleFilter}
              role="button"
              aria-expanded={isFilterOpen}
              className="filterButton position-relative cursor-pointer"
            >
              {svg?.filterIcon}
              {getFilterCount() > 0 && (
                <span className="position-absolute badge customBadgeFont rounded-pill bg-danger">
                  {getFilterCount()}
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          className={`filter-transition-container overflow-hidden ${isFilterOpen ? "is-open mb-3" : ""
            }`}
        >
          <div className="d-flex flex-wrap align-items-end bg-white p-2 rounded-3 position-relative">
            <label className="filterTitle fw-bold fs-5 position-absolute d-flex align-items-center">
              Filters
            </label>
            <div className="row gx-md-3 gx-2 gy-sm-3 gy-2 w-100">
              <div className="col-lg-auto col-md-4 col-sm-6">
                <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                  Type
                </label>
                <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                  <Select
                    value={selectedLeaveType}
                    className="form-select form-select-lg"
                    onChange={(value) => setSelectedLeaveType(value)}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option: any) =>
                      String(option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    <Select.Option value="">All</Select.Option>
                    {leaveTypes.map((type: any) => (
                      <Select.Option key={type._id} value={type._id}>
                        {type.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="col-lg-auto col-md-4 col-sm-6">
                <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                  Action
                </label>
                <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                  <Select
                    value={selectedAction}
                    className="form-select form-select-lg"
                    onChange={(value) => setSelectedAction(value)}
                  >
                    <Select.Option value="">All</Select.Option>
                    <Select.Option value="deducted">Deducted (Approved)</Select.Option>
                    <Select.Option value="reverted">Reverted (Rejected)</Select.Option>
                  </Select>
                </div>
              </div>

              {/* Year Dropdown moved inside filters after Action */}
              <div className="col-lg-auto col-md-4 col-sm-6">
                <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                  Year
                </label>
                <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                  <Select
                    value={selectedYear ? Number(selectedYear) : undefined}
                    onChange={(val) => handleYearFilterChange(String(val))}
                    className="form-select form-select-lg"
                    suffixIcon={svg?.selectDownArrow}
                  >
                    {yearOptions.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>

              {getFilterCount() > 0 && (
                <ClearComponent handleClick={clearFilters} />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="card page-table-card border-0 shadow rounded-4 flex-fill overflow-y-auto">
        <div id="ptoHistoryScrollableDiv" className="card-body p-0 overflow-y-auto h-100">
          {loading && currentPage === 1 ? (
            <FullScreenLoader />
          ) : data?.length === 0 ? (
            <NoDataMessage
              icon={svg?.hrSettings?.pto?.empty}
              text="You do not have any Paid Time Off history yet."
            />
          ) : (
            <InfiniteScroll
              dataLength={data.length}
              next={loadMoreData}
              hasMore={hasMore}
              loader={
                loading && currentPage > 1 ? (
                  <div className="text-center p-2">
                    <Spin />
                  </div>
                ) : null
              }
              scrollableTarget="ptoHistoryScrollableDiv"
              height={"100%"}
              style={{ overflow: "auto" }}
            >
              <Table
                className="common-table"
                columns={columns}
                dataSource={tableData}
                loading={loading && currentPage === 1}
                rowKey="_id"
                pagination={false}
              />
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
};

export default PtoHistory;
